<script setup lang="ts">
import { Dialog, Listbox, Select } from 'primevue';
import type { AuditResponse } from '../api/auditResponse';
import { computed, ref } from 'vue';
import type { AuditTrailResponse } from '../api/auditTrailResponse';
import { useLanguageStore } from '../../translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import type { AuditLogResponse } from '../api/auditLogResponse';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { AuditTrailAction } from '../domain/auditTrailAction';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const visible = ref(false);
const audit = ref<AuditResponse>();
const selectedTrail = ref<AuditTrailResponse>();
const trails = computed(() => audit.value?.trails ?? []);

const dialogTitle = computed(() => {
  if (audit.value === undefined) return '';
  return translations.value.audit.dialog.title(audit.value.title);
});

const title = computed(() => {
  const trail = selectedTrail.value;
  if (trail === undefined) return '';
  return trailName(trail);
});

const duration = computed(() => {
  const trail = selectedTrail.value;
  if (trail === undefined) return '';
  const date = languageStore.formatDate(trail.startTime);
  const duration = languageStore.formatDateRange(trail.startTime, trail.endTime);
  return `${date} (${duration})`
});

const causedBy = computed(() => {
  if (selectedTrail.value === undefined) return '';

  const user = selectedTrail.value.causedByUser ?? '';
  const app = selectedTrail.value.causedByApp;

  if (app && app.length > 0) {
    return `${user} (${app})`;
  }

  return user;
});

const selectedLog = ref<AuditLogResponse>();

const properties = computed<Property[]>(() => {
  if (selectedLog.value === undefined) return [];

  const map = new Map<string, Property>();

  const {
    oldValues,
    newValues
  } = selectedLog.value;

  if (oldValues) {
    for (const [name, value] of Object.entries(oldValues)) {
      let prop = map.get(name);
      if (!prop) {
        prop = {
          name: name,
          old: null,
          new: null
        };
        map.set(name, prop);
      }
      prop.old = value;
    }
  }

  if (newValues) {
    for (const [name, value] of Object.entries(newValues)) {
      let prop = map.get(name);
      if (!prop) {
        prop = {
          name: name,
          old: null,
          new: null
        };
        map.set(name, prop);
      }
      prop.new = value;
    }
  }

  return Array.from(map.values());
});

defineExpose({
  show
});

function show(options: ShowOptions) {

  audit.value = options.audit;

  if (options.audit.trails.length > 0) {
    selectedTrail.value = options.audit.trails[0];
  }

  if (selectedTrail.value !== undefined) {
    if (selectedTrail.value.logs.length > 0) {
      selectedLog.value = selectedTrail.value.logs[0];
    }
  }

  visible.value = true;
}

function onSelectedTrailChanged() {
  if (selectedTrail.value !== undefined) {
    if (selectedTrail.value.logs.length > 0) {
      selectedLog.value = selectedTrail.value.logs[0];
    }
  }
}

function onHide() {
  selectedTrail.value = undefined;
  selectedLog.value = undefined;
  audit.value = undefined;
  visible.value = false;
}



function getLogTitle(log: AuditLogResponse) {

  if (log.title && log.title.length > 0) {
    return `${log.title} (${log.entityName}) #${log.id} ${log.changeType} ${languageStore.formatDate(log.timestamp)}`;
  }

  return `${log.entityName} #${log.id} ${log.changeType}`;
}

export interface ShowOptions {
  audit: AuditResponse;
}

interface Property {
  name: string;
  old: any | null;
  new: any | null;
}

function trailName(trail: AuditTrailResponse) {
  if (trail.action === AuditTrailAction.Undefined && trail.title) {
    return trail.title;
  }

  return translations.value.enums.auditTrailAction(trail.action);
}

</script>

<template>
  <Dialog v-model:visible="visible"
    :header="dialogTitle"
    @hide="onHide"
    class="audit-dialog">
    <div class="audit-grid">
      <Listbox
        :filter="true"
        v-model:model-value="selectedTrail"
        :options="trails"
        :option-label="trailName"
        data-key="id"
        scroll-height="auto"
        class="trail-list"
        @change="onSelectedTrailChanged">
        <template #option="trail">
          <div class="trail-row">
            <span class="trail-name-column">{{ trailName(trail.option) }}</span>
            <span class="trail-date-column">{{ languageStore.formatDate(trail.option.startTime) }}</span>
          </div>

        </template>
      </Listbox>
      <div v-if="selectedTrail" class="trail-panel">
        <h4 class="text-xl">{{ title }}</h4>
        <span>{{ duration }}</span>
        <span>{{ causedBy }}</span>
        <p>{{ selectedTrail.description }}</p>

        <div class="row-panel" v-if="selectedTrail.logs.length > 0">
          <Select v-model:model-value="selectedLog" :options="selectedTrail.logs" :option-label="getLogTitle" data-key="id"></Select>
          <DataTable scrollable class="properties-table" :value="properties">
            <Column class="name-column" field="name" header="Name"  style="width: 14rem"></Column>
            <Column field="old" header="Old" class=""></Column>
            <Column field="new" header="New"></Column>
          </DataTable>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>

.audit-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 1rem;
  overflow: hidden;
}

.trail-list {
  min-width: 20rem;
}

.trail-panel {
  display: grid;
  grid-template-rows: repeat(4, auto) 1fr;
  overflow-x: hidden;
  row-gap: .25rem;
}

.row-panel {
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;
}

.properties-table {
  overflow-y: hidden;
  display: grid;
}

:deep(.name-column) {
  font-weight: 500;
}

:deep(.trail-row) {
  display: grid;
  grid-template-columns: 1fr auto;
  width: 100%;
}

:deep(.trail-date-column) {
  font-size: 12px;
  align-self: center;
  color: var(--p-text-muted-color);
}

:deep(.trail-name-column), :deep(.trail-date-column) {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

</style>

<style>
.audit-dialog {
  min-height: 90%;
  width: 90%;
  display: grid!important;
  grid-template-rows: auto 1fr!important;
}

.audit-dialog .p-dialog-content {
  display: grid;
  overflow: hidden;
}

.audit-dialog .p-listbox {
  overflow-y: auto;
}

.audit-dialog td {
  overflow-wrap: break-word;
}

.audit-dialog .p-datatable-table {
  table-layout: fixed;
}



</style>
